import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Table, Button, Input, Select, Space, Popconfirm, message, Switch, Tag } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ClearOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { makeGetCall, makePostCall, makePutCall, makeDeleteCall } from '../../utils/helper';
import { getUserDetails } from '../../utils/common';
import DateFormatter from '../../components/DateFormatter';
import EditCenter from './EditCenter';
import './center-list.css';

const { Option } = Select;

const CenterList = () => {
  const [centersList, setCentersList] = useState({ data: [], loading: false, error: null });
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedCenter, setSelectedCenter] = useState(null);

  // Filter states
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Fetch centers list
  const fetchCenters = useCallback(async () => {
    setCentersList(prev => ({ ...prev, loading: true, error: null }));
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('active', statusFilter === 'active' ? 'true' : 'false');
      }
      if (searchText.trim()) {
        params.append('search', searchText.trim());
      }

      const url = `/centres${params.toString() ? '?' + params.toString() : ''}`;
      const response = await makeGetCall(url);

      if (response.data.success) {
        setCentersList({
          data: response.data.data || [],
          loading: false,
          error: null,
        });
      } else {
        throw new Error(response.data.message || 'Failed to fetch centers');
      }
    } catch (error) {
      setCentersList({
        data: [],
        loading: false,
        error: error.message || 'Failed to fetch centers',
      });
      message.error('Failed to fetch centers');
    }
  }, [statusFilter, searchText]);

  useEffect(() => {
    fetchCenters();
  }, [fetchCenters]);

  // Handle edit center
  const handleEdit = (record) => {
    setSelectedCenter(record);
    setEditModalVisible(true);
  };

  // Handle delete center
  const handleDelete = async (record, permanent = false) => {
    setDeleteInProgress(true);
    try {
      const url = permanent
        ? `/centres/${record.id}?permanent=true`
        : `/centres/${record.id}`;

      await makeDeleteCall(url);

      message.success(
        permanent
          ? 'Center permanently deleted successfully'
          : 'Center deleted successfully'
      );
      fetchCenters();
    } catch (error) {
      message.error(error.message || 'Failed to delete center');
    } finally {
      setDeleteInProgress(false);
    }
  };

  // Handle restore center
  const handleRestore = async (record) => {
    setDeleteInProgress(true);
    try {
      await makePutCall(`/centres/${record.id}`, {
        ...record,
        active: true,
      });

      message.success('Center restored successfully');
      fetchCenters();
    } catch (error) {
      message.error(error.message || 'Failed to restore center');
    } finally {
      setDeleteInProgress(false);
    }
  };

  // Handle update after edit
  const handleUpdateSuccess = () => {
    setEditModalVisible(false);
    setSelectedCenter(null);
    fetchCenters();
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchText('');
    setStatusFilter('all');
  };

  // Filtered centers
  const filteredCenters = useMemo(() => {
    return centersList.data;
  }, [centersList.data]);

  // Table columns
  const columns = [
    {
      title: 'Center Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Center Code',
      dataIndex: 'code',
      key: 'code',
      sorter: (a, b) => a.code.localeCompare(b.code),
    },
    {
      title: 'IP Address',
      dataIndex: 'ip',
      key: 'ip',
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: 'Contact',
      dataIndex: 'contact',
      key: 'contact',
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value, record) => record.active === value,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => <DateFormatter date={date} />,
      sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            disabled={!record.active}
            title="Edit Center"
          >
            Edit
          </Button>
          {record.active ? (
            <>
              <Popconfirm
                title="Are you sure you want to delete this center?"
                onConfirm={() => handleDelete(record, false)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deleteInProgress}
                  title="Delete Center"
                >
                  Delete
                </Button>
              </Popconfirm>
              <Popconfirm
                title="Are you sure you want to permanently delete this center?"
                description="This action cannot be undone."
                onConfirm={() => handleDelete(record, true)}
                okText="Yes"
                cancelText="No"
                okButtonProps={{ danger: true }}
              >
                <Button
                  type="link"
                  danger
                  loading={deleteInProgress}
                  title="Permanently Delete"
                >
                  Permanent Delete
                </Button>
              </Popconfirm>
            </>
          ) : (
            <Popconfirm
              title="Are you sure you want to restore this center?"
              onConfirm={() => handleRestore(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="link"
                icon={<UndoOutlined />}
                loading={deleteInProgress}
                title="Restore Center"
              >
                Restore
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="admin-container center-list-container">
      <div className="admin-header">
        <h2>Center Management</h2>
      </div>

      <div className="admin-filters">
        <Space size="middle" wrap>
          <Input
            placeholder="Search by name, code, or location"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />

          <Select
            placeholder="Filter by Status"
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 150 }}
          >
            <Option value="all">All Status</Option>
            <Option value="active">Active</Option>
            <Option value="inactive">Inactive</Option>
          </Select>

          <Button
            icon={<ClearOutlined />}
            onClick={handleClearFilters}
          >
            Clear Filters
          </Button>
        </Space>
      </div>

      {centersList.error && (
        <div className="error-message">
          <p>{centersList.error}</p>
          <Button onClick={fetchCenters}>Retry</Button>
        </div>
      )}

      <div className="admin-table-container">
        <Table
          columns={columns}
          dataSource={filteredCenters}
          loading={centersList.loading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} centers`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          scroll={{ x: 'max-content' }}
        />
      </div>

      {editModalVisible && selectedCenter && (
        <EditCenter
          visible={editModalVisible}
          center={selectedCenter}
          onCancel={() => {
            setEditModalVisible(false);
            setSelectedCenter(null);
          }}
          onSuccess={handleUpdateSuccess}
        />
      )}
    </div>
  );
};

export default CenterList;
